import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Search } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { FAQ_GROUPS } from '../components/faq/faqData';

const jsonLd = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ_GROUPS.flatMap((g) => g.items.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))) });

const Group = ({ g }) => (
  <section id={g.id} className="scroll-mt-28" data-testid={`faq-group-${g.id}`}>
    <h2 className="font-outfit text-xl font-semibold text-[#0b1f4d] mb-3">{g.title}</h2>
    <Accordion type="multiple" className="rounded-2xl bg-white border border-slate-200 px-5">
      {g.items.map(([q, a], i) => (
        <AccordionItem key={q} value={q} className={i === g.items.length - 1 ? 'border-b-0' : ''}>
          <AccordionTrigger className="text-left text-[15px] font-semibold text-[#0b1f4d] hover:no-underline hover:text-teal-700" data-testid={`faq-q-${g.id}-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed text-[15px]" data-testid={`faq-a-${g.id}-${i}`}>{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function FAQ() {
  const [q, setQ] = useState('');
  useEffect(() => { document.title = 'FAQ | Elevate Careers'; }, []);
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return FAQ_GROUPS;
    return FAQ_GROUPS.map((g) => ({ ...g, items: g.items.filter(([a, b]) => `${a} ${b}`.toLowerCase().includes(t)) })).filter((g) => g.items.length);
  }, [q]);
  return (
    <main className="bg-slate-50" data-testid="faq-page">
      <Helmet><script type="application/ld+json">{jsonLd}</script></Helmet>
      <section className="bg-[#0b1f4d] text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-32 pb-16">
          <p className="text-[11px] font-semibold tracking-[0.25em] uppercase mb-4 text-teal-300">Help centre</p>
          <h1 className="font-outfit font-bold text-4xl sm:text-5xl">Frequently asked questions</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Everything about joining, the Readiness Check, consultations, programs, payments and your dashboard.</p>
          <div className="relative mt-8 max-w-xl">
            <Search className="w-5 h-5 absolute left-4 top-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions, e.g. refund, login, price" data-testid="faq-search"
              className="w-full rounded-full bg-white text-[#0b1f4d] pl-12 pr-5 py-3.5 outline-none focus:ring-2 focus:ring-teal-400" />
          </div>
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[200px_1fr] gap-10">
        <nav className="hidden lg:block sticky top-28 self-start space-y-1" data-testid="faq-nav">
          {FAQ_GROUPS.map((g) => <a key={g.id} href={`#${g.id}`} className="block text-sm text-slate-600 hover:text-teal-700 py-1.5" data-testid={`faq-nav-${g.id}`}>{g.title}</a>)}
        </nav>
        <div className="space-y-10">
          {groups.map((g) => <Group key={g.id} g={g} />)}
          {!groups.length && <p className="text-slate-500" data-testid="faq-no-results">No questions match "{q}". Ask us on WhatsApp below.</p>}
          <div className="rounded-3xl bg-[#0b1f4d] text-white p-8 flex flex-wrap items-center justify-between gap-5" data-testid="faq-contact">
            <div><p className="font-outfit text-xl font-semibold">Still have a question?</p><p className="text-slate-300 text-sm mt-1">Our team usually replies within a few hours.</p></div>
            <div className="flex gap-3">
              <a href="https://wa.me/919311510040" target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb658] font-semibold px-5 py-3 rounded-full transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
              <Link to="/contact" data-testid="faq-contact-link" className="border border-white/25 hover:bg-white/10 font-semibold px-5 py-3 rounded-full transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
